import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useGetListOfValuesQuery } from 'common/api/lovApiSlice';
import { BaseMasterDataItem } from 'common/api/masterDataTypes';
import { LOVHookReturn } from 'types/lovDataTypes';
import { RootState } from 'store';

export const useLovData = (): LOVHookReturn => {
  // Get current user from auth state
  const { userId, isAuthenticated } = useSelector((state: RootState) => ({
    userId: state.auth?.userId || '',
    isAuthenticated: state.auth?.isAuthenticated || false,
  }));

  const { 
    data: responseData, 
    isLoading, 
    isSuccess,
    error: apiError,
    refetch
  } = useGetListOfValuesQuery(
    { userId: isAuthenticated ? userId : undefined },
    {
      // Only fetch if user is authenticated
      skip: !isAuthenticated,
      // Refetch when auth state changes
      refetchOnMountOrArgChange: false,
    }
  );

  // Data is already processed by RTK Query transformResponse
  const rawData = responseData?.rawData || [];
  const processedData = responseData?.processedData || null;
  const error = apiError ? 'Failed to fetch LOV data' : (!isAuthenticated ? 'User not authenticated' : null);

  // Convenience methods
  const getByType = useCallback((type: string): BaseMasterDataItem[] => {
    if (!processedData) return [];
    return processedData.categorized[type] || [];
  }, [processedData]);

  const searchByName = useCallback((name: string): BaseMasterDataItem | null => {
    if (!processedData?.indexes.nameIndex) return null;
    const nameMap = new Map(processedData.indexes.nameIndex);
    return nameMap.get(name) || null;
  }, [processedData]);

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    rawData,
    processedData,
    isLoading: isAuthenticated ? isLoading : false,
    isProcessing: false, // No more background processing!
    isReady: isAuthenticated && !isLoading && !!processedData && !error,
    processingProgress: { processed: 0, total: 0, percentage: 0 }, // Not needed anymore
    getByType,
    searchByName,
    error,
    retry,
  };
}; 